import React, { useState, useEffect } from "react";
import { Add20Regular, Dismiss20Regular, Checkmark20Regular } from '@fluentui/react-icons';
import Header from "./Header";
import AIAssistant from "./AIAssistant";
import ContactInfo from "./ContactInfo";
import PropertyInfo from "./PropertyInfo";
import IncidentInfo from "./IncidentInfo";
import contactService from '../services/contactService';
import contactDetailsService from '../services/contactDetailsService';
import { useLanguage } from '../contexts/LanguageContext';

export default function Dashboard({ user, onLogout, onRefresh, isRefreshing, contextVersion, onSocketResetReady }) {
  const [openSection, setOpenSection] = useState("contact");
  const [showContactDetail, setShowContactDetail] = useState(false);
  const [contactDetailData, setContactDetailData] = useState(null);
  const [isEmailIndexed, setIsEmailIndexed] = useState(true);
  const { t } = useLanguage();

  if (showContactDetail) {
    return <ContactDetail onClose={() => setShowContactDetail(false)} contactData={contactDetailData} />;
  }

  // const handleOfficeJsItem = () => {
  //   const item = Office.context.mailbox.item;
  //   console.log(item);
  // };

  return (
    <div className="bg-white min-h-screen font-sans flex flex-col">
      <Header user={user} onLogout={onLogout} onRefresh={onRefresh} isRefreshing={isRefreshing} />

      {/* <button onClick={handleOfficeJsItem}>test officeJs item</button> */}

      {/* AI Assistant Section */}
      <AIAssistant 
        contextVersion={contextVersion} 
        onSocketResetReady={onSocketResetReady} 
        onIndexStatusChange={setIsEmailIndexed}
      />

      {/* Expandable Sections - Only show if email is indexed */}
      {isEmailIndexed && (
        <div className="flex-1 overflow-y-auto">
          <ContactInfo
            isOpen={openSection === "contact"}
            onToggle={() => setOpenSection(openSection === "contact" ? null : "contact")}
            onShowDetail={(contactData) => {
              setContactDetailData(contactData);
              setShowContactDetail(true);
            }}
            contextVersion={contextVersion}
          />
          <PropertyInfo
            isOpen={openSection === "property"}
            onToggle={() => setOpenSection(openSection === "property" ? null : "property")}
            contextVersion={contextVersion}
          />
          <IncidentInfo
            isOpen={openSection === "incident"}
            onToggle={() => setOpenSection(openSection === "incident" ? null : "incident")}
            contextVersion={contextVersion}
          />
        </div>
      )}
    </div>
  );
}

function ContactDetail({ onClose, contactData }) {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    salutation: contactData?.salutation || '',
    name: contactData?.name || '',
    surname: contactData?.surname || '',
    type: contactData?.type || '',
    sub_type: contactData?.sub_type || '',
    street_name: contactData?.street_name || '',
    house_number: contactData?.house_number || '',
    zip_code: contactData?.zip_code || '',
    city: contactData?.city || '',
    district: contactData?.district || '',
    country: contactData?.country || '',
    preffered_contact_type: contactData?.preffered_contact_type || '',
    notes: contactData?.notes || '',
    external_id: contactData?.external_id || ''
  });

  const [contactDetails, setContactDetails] = useState({ emails: [], phones: [] });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savingDetails, setSavingDetails] = useState({}); // Track saving state for each detail
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Fetch full contact details with IDs when component mounts
  useEffect(() => {
    const fetchContactDetails = async () => {
      if (!contactData?.id) return;
      
      setLoadingDetails(true);
      try {
        const result = await contactDetailsService.getContactDetailsByContactId(contactData.id);
        console.log('Fetched contact details response:', result); // Debug log
        
        // The response has nested data: response.data.data
        const contactDetailsArray = result.data?.data || result.data || [];
        console.log('Contact details array:', contactDetailsArray); // Debug log
        
        if (result.success && contactDetailsArray.length > 0) {
          // Group by type
          const emails = contactDetailsArray
            .filter(d => d.type === 'email')
            .map((e, idx) => ({
              id: e.id,
              value: e.value,
              label: e.label || 'primary',
              tempId: `email-${e.id}-${idx}`,
              hasChanges: false
            }));
          
          const phones = contactDetailsArray
            .filter(d => d.type === 'phone')
            .map((p, idx) => ({
              id: p.id,
              value: p.value,
              label: p.label || 'primary',
              tempId: `phone-${p.id}-${idx}`,
              hasChanges: false
            }));
          
          console.log('Emails with IDs:', emails); // Debug log
          console.log('Phones with IDs:', phones); // Debug log
          
          setContactDetails({ emails, phones });
        } else {
          // No contact details found, initialize empty arrays
          console.log('No contact details found, initializing empty arrays');
          setContactDetails({ emails: [], phones: [] });
        }
      } catch (error) {
        console.error('Failed to fetch contact details:', error);
        // Initialize with empty arrays on error
        setContactDetails({ emails: [], phones: [] });
      } finally {
        setLoadingDetails(false);
      }
    };
    
    fetchContactDetails();
  }, [contactData?.id]);

  if (!contactData) {
    return (
      <div className="h-screen flex items-center justify-center">
        <p className="text-gray-500">{t('contactDetail.noData')}</p>
      </div>
    );
  }

  const getInitials = () => {
    const firstInitial = formData.name ? formData.name.charAt(0).toUpperCase() : '';
    const lastInitial = formData.surname ? formData.surname.charAt(0).toUpperCase() : '';
    return firstInitial + lastInitial || 'C';
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddEmail = () => {
    const tempId = `email-new-${Date.now()}`;
    const newEmail = { id: null, value: '', label: 'primary', tempId, hasChanges: false };
    
    setContactDetails(prev => ({
      ...prev,
      emails: [...prev.emails, newEmail]
    }));
  };

  const handleAddPhone = () => {
    const tempId = `phone-new-${Date.now()}`;
    const newPhone = { id: null, value: '', label: 'primary', tempId, hasChanges: false };
    
    setContactDetails(prev => ({
      ...prev,
      phones: [...prev.phones, newPhone]
    }));
  };

  const handleRemoveEmail = async (tempId) => {
    const emailToRemove = contactDetails.emails.find(e => e.tempId === tempId);
    console.log('Removing email:', emailToRemove); // Debug log
    
    // If it has an ID, delete from backend
    if (emailToRemove?.id) {
      console.log('Email has ID, deleting from backend:', emailToRemove.id); // Debug log
      setSavingDetails(prev => ({ ...prev, [tempId]: true }));
      try {
        await contactDetailsService.deleteContactDetail(emailToRemove.id);
        console.log('Successfully deleted email'); // Debug log
      } catch (error) {
        console.error('Failed to delete email:', error);
        setSaveError(t('contactDetail.deleteError') || 'Failed to delete email');
        setSavingDetails(prev => ({ ...prev, [tempId]: false }));
        return;
      }
      setSavingDetails(prev => ({ ...prev, [tempId]: false }));
    } else {
      console.log('Email has no ID, just removing from state'); // Debug log
    }
    
    // Remove from state
    setContactDetails(prev => ({
      ...prev,
      emails: prev.emails.filter(e => e.tempId !== tempId)
    }));
  };

  const handleRemovePhone = async (tempId) => {
    const phoneToRemove = contactDetails.phones.find(p => p.tempId === tempId);
    console.log('Removing phone:', phoneToRemove); // Debug log
    
    // If it has an ID, delete from backend
    if (phoneToRemove?.id) {
      console.log('Phone has ID, deleting from backend:', phoneToRemove.id); // Debug log
      setSavingDetails(prev => ({ ...prev, [tempId]: true }));
      try {
        await contactDetailsService.deleteContactDetail(phoneToRemove.id);
        console.log('Successfully deleted phone'); // Debug log
      } catch (error) {
        console.error('Failed to delete phone:', error);
        setSaveError(t('contactDetail.deleteError') || 'Failed to delete phone');
        setSavingDetails(prev => ({ ...prev, [tempId]: false }));
        return;
      }
      setSavingDetails(prev => ({ ...prev, [tempId]: false }));
    } else {
      console.log('Phone has no ID, just removing from state'); // Debug log
    }
    
    // Remove from state
    setContactDetails(prev => ({
      ...prev,
      phones: prev.phones.filter(p => p.tempId !== tempId)
    }));
  };

  const handleEmailChange = (tempId, value) => {
    // Update state and mark as changed
    setContactDetails(prev => ({
      ...prev,
      emails: prev.emails.map(e => 
        e.tempId === tempId ? { ...e, value, hasChanges: true } : e
      )
    }));
  };

  const handlePhoneChange = (tempId, value) => {
    // Update state and mark as changed
    setContactDetails(prev => ({
      ...prev,
      phones: prev.phones.map(p => 
        p.tempId === tempId ? { ...p, value, hasChanges: true } : p
      )
    }));
  };

  const handleSaveEmail = async (tempId) => {
    const email = contactDetails.emails.find(e => e.tempId === tempId);
    if (!email || !email.value || !email.value.trim()) {
      setSaveError(t('contactDetail.emptyValueError') || 'Email address is required');
      return;
    }
    
    setSavingDetails(prev => ({ ...prev, [tempId]: true }));
    setSaveError(null);
    
    try {
      const detailData = {
        contact_id: contactData.id,
        type: 'email',
        value: email.value,
        label: email.label || 'primary',
        organisation_id: contactData.organisation_id
      };

      if (email.id) {
        // Update existing
        await contactDetailsService.updateContactDetail(email.id, detailData);
      } else {
        // Create new
        const result = await contactDetailsService.createContactDetail(detailData);
        // Update the email with the new ID from backend
        setContactDetails(prev => ({
          ...prev,
          emails: prev.emails.map(e => 
            e.tempId === tempId ? { ...e, id: result.data.id, hasChanges: false } : e
          )
        }));
      }
      
      // Mark as saved
      setContactDetails(prev => ({
        ...prev,
        emails: prev.emails.map(e => 
          e.tempId === tempId ? { ...e, hasChanges: false } : e
        )
      }));
    } catch (error) {
      console.error('Failed to save email:', error);
      setSaveError(t('contactDetail.saveDetailError') || 'Failed to save email');
    } finally {
      setSavingDetails(prev => ({ ...prev, [tempId]: false }));
    }
  };

  const handleSavePhone = async (tempId) => {
    const phone = contactDetails.phones.find(p => p.tempId === tempId);
    if (!phone || !phone.value || !phone.value.trim()) {
      setSaveError(t('contactDetail.emptyValueError') || 'Phone number is required');
      return;
    }
    
    setSavingDetails(prev => ({ ...prev, [tempId]: true }));
    setSaveError(null);
    
    try {
      const detailData = {
        contact_id: contactData.id,
        type: 'phone',
        value: phone.value,
        label: phone.label || 'primary',
        organisation_id: contactData.organisation_id
      };

      if (phone.id) {
        // Update existing
        await contactDetailsService.updateContactDetail(phone.id, detailData);
      } else {
        // Create new
        const result = await contactDetailsService.createContactDetail(detailData);
        // Update the phone with the new ID from backend
        setContactDetails(prev => ({
          ...prev,
          phones: prev.phones.map(p => 
            p.tempId === tempId ? { ...p, id: result.data.id, hasChanges: false } : p
          )
        }));
      }
      
      // Mark as saved
      setContactDetails(prev => ({
        ...prev,
        phones: prev.phones.map(p => 
          p.tempId === tempId ? { ...p, hasChanges: false } : p
        )
      }));
    } catch (error) {
      console.error('Failed to save phone:', error);
      setSaveError(t('contactDetail.saveDetailError') || 'Failed to save phone');
    } finally {
      setSavingDetails(prev => ({ ...prev, [tempId]: false }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);

    try {
      // Update main contact data only
      const contactUpdateData = {
        ...formData,
        organisation_id: contactData.organisation_id
      };

      await contactService.updateContact(contactData.id, contactUpdateData);

      // Success - close the detail view immediately
      console.log('✅ Contact saved successfully!');
      onClose();
    } catch (error) {
      console.error('Error saving contact:', error);
      setSaveError(error.message);
      setSaving(false);
    }
  };

  return (
    <div className="h-screen flex flex-col animate-fade-in">
      <div className="p-4 bg-gradient-to-r from-blue-600 to-blue-500">
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            className="h-8 w-8 text-white hover:bg-white/20 rounded flex items-center justify-center"
          >
            ←
          </button>
          <div className="h-12 w-12 bg-white text-blue-600 rounded-full flex items-center justify-center font-semibold">
            {getInitials()}
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-white">
              {formData.salutation && `${formData.salutation} `}
              {formData.name} {formData.surname}
            </h2>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {saveError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-xs text-red-600">{t('contactDetail.saveError')}: {saveError}</p>
          </div>
        )}

        <div className="space-y-4">
          <h3 className="font-semibold text-sm uppercase tracking-wide text-gray-500">
            {t('contactDetail.basicInformation')}
          </h3>

          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">{t('contactDetail.salutation')}</label>
            <input
              type="text"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.salutation}
              onChange={(e) => handleInputChange('salutation', e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-700">{t('contactDetail.name')}</label>
              <input
                type="text"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-700">{t('contactDetail.surname')}</label>
              <input
                type="text"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.surname}
                onChange={(e) => handleInputChange('surname', e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-semibold text-sm uppercase tracking-wide text-gray-500">{t('contactDetail.address')}</h3>

          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">{t('contactDetail.streetHouseNumber')}</label>
            <div className="grid grid-cols-3 gap-2">
              <input
                type="text"
                className="col-span-2 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.street_name}
                onChange={(e) => handleInputChange('street_name', e.target.value)}
                placeholder={t('contactDetail.street')}
              />
              <input
                type="text"
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.house_number}
                onChange={(e) => handleInputChange('house_number', e.target.value)}
                placeholder={t('contactDetail.number')}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-700">{t('contactDetail.zipCode')}</label>
              <input
                type="text"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.zip_code}
                onChange={(e) => handleInputChange('zip_code', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="block text-xs font-medium text-gray-700">{t('contactDetail.city')}</label>
              <input
                type="text"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">{t('contactDetail.district')}</label>
            <input
              type="text"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.district}
              onChange={(e) => handleInputChange('district', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">{t('contactDetail.country')}</label>
            <input
              type="text"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.country}
              onChange={(e) => handleInputChange('country', e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-4">
          <h3 className="font-semibold text-sm uppercase tracking-wide text-gray-500">
            {t('contactDetail.contactInformation')}
          </h3>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-medium text-gray-700">{t('contactDetail.email')}</label>
              <button
                onClick={handleAddEmail}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                disabled={loadingDetails}
              >
                <Add20Regular className="h-3 w-3" />
                {t('contactDetail.addEmail')}
              </button>
            </div>
            {loadingDetails ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-xs text-gray-600">{t('contactDetail.loading')}</span>
              </div>
            ) : contactDetails.emails.map((email) => (
              <div key={email.tempId} className="flex gap-2 items-center">
                <input
                  type="email"
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={email.value}
                  onChange={(e) => handleEmailChange(email.tempId, e.target.value)}
                  placeholder={t('contactDetail.emailAddress')}
                  disabled={savingDetails[email.tempId]}
                />
                {savingDetails[email.tempId] ? (
                  <div className="p-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                ) : email.hasChanges ? (
                  <button
                    onClick={() => handleSaveEmail(email.tempId)}
                    className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded"
                    title={t('contactDetail.save')}
                  >
                    <Checkmark20Regular className="h-4 w-4" />
                  </button>
                ) : null}
                <button
                  onClick={() => handleRemoveEmail(email.tempId)}
                  className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                  disabled={savingDetails[email.tempId]}
                >
                  <Dismiss20Regular className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-medium text-gray-700">{t('contactDetail.phone')}</label>
              <button
                onClick={handleAddPhone}
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                disabled={loadingDetails}
              >
                <Add20Regular className="h-3 w-3" />
                {t('contactDetail.addPhone')}
              </button>
            </div>
            {loadingDetails ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-xs text-gray-600">{t('contactDetail.loading')}</span>
              </div>
            ) : contactDetails.phones.map((phone) => (
              <div key={phone.tempId} className="flex gap-2 items-center">
                <input
                  type="tel"
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={phone.value}
                  onChange={(e) => handlePhoneChange(phone.tempId, e.target.value)}
                  placeholder={t('contactDetail.phoneNumber')}
                  disabled={savingDetails[phone.tempId]}
                />
                {savingDetails[phone.tempId] ? (
                  <div className="p-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                ) : phone.hasChanges ? (
                  <button
                    onClick={() => handleSavePhone(phone.tempId)}
                    className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded"
                    title={t('contactDetail.save')}
                  >
                    <Checkmark20Regular className="h-4 w-4" />
                  </button>
                ) : null}
                <button
                  onClick={() => handleRemovePhone(phone.tempId)}
                  className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded"
                  disabled={savingDetails[phone.tempId]}
                >
                  <Dismiss20Regular className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>

        </div>

        <div className="space-y-4">
          <h3 className="font-semibold text-sm uppercase tracking-wide text-gray-500">
            {t('contactDetail.additionalInformation')}
          </h3>

          <div className="space-y-2">
            <label className="block text-xs font-medium text-gray-700">{t('contactDetail.notes')}</label>
            <textarea
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md min-h-[100px] focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2 pt-4 border-t pb-4 sticky bottom-0 bg-white">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-blue-600 text-white font-medium py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? t('contactDetail.saving') : t('contactDetail.saveChanges')}
          </button>
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('contactDetail.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
